import { DocumentChartBarIcon, DocumentIcon, InformationCircleIcon, PlusIcon } from "@heroicons/react/24/outline";
import { HIDDEN_FILE_KINDS } from "@utils/constants";
import { nonNullable } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import { Button, Dialog, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { AssessmentToolType, LearnerFileType, useAssessmentToolsQuery } from "@api/graphql/types-and-hooks";
import { UploadDocuments } from "@components/UploadDocuments/UploadDocuments";
import { useGetRemoveFileCallback } from "@hooks/useLearnerFileUpload";
import { FilesCollapsible } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/FilesCollapsible";
import { AssessmentSelection } from "@pages/ClinicalReportDetails/UploadFiles/Assessments/Assessments";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";

interface AssessmentReportsCollapsibleProps {
  isReadOnly?: boolean;
  handleFileClicked?: (learnerFile: LearnerFileType) => void;
}
export const AssessmentReportsCollapsible: React.FC<AssessmentReportsCollapsibleProps> = ({
  isReadOnly = false,
  handleFileClicked,
}) => {
  const {
    assessmentFiles,
    downloadReportFileById,
    isReportCompleted,
    isDiagnosisEvaluation,
    isReassessment,
    reportType,
  } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();
  const flags = useFlags();
  const isDbBulkUploadEnabled = isReassessment || (flags["db-singlepage-bulkupload"] && isDiagnosisEvaluation);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [isAddAssessmentDialogOpen, setAddAssessmentDialogOpen] = useState<boolean>(false);

  const [fileIdToDelete, setFileIdToDelete] = useState<string | null>(null);

  // Query for supported assessments (needed for bulk uploader)
  const { data: assessmentToolsData } = useAssessmentToolsQuery({ reportType });
  const supportedAssessmentsList: AssessmentToolType[] = useMemo(
    () => assessmentToolsData?.assessmentTools?.filter((item) => !!item).filter((tool) => tool.parsingSupported) ?? [],
    [assessmentToolsData],
  );

  // Get existing files for bulk uploader
  const existingFiles = useMemo(
    () =>
      report.reportFiles.edges
        ?.map((edge) => edge?.node)
        .filter(nonNullable)
        .filter((file) => file.status !== "PENDING")
        .filter((file) => !HIDDEN_FILE_KINDS.includes(file.fileKind)) as LearnerFileType[],
    [report],
  );

  const handleRemoveFile = (fileId: string) => {
    setConfirmDialogOpen(true);
    setFileIdToDelete(fileId);
  };

  const getRemoveFileCallback = useGetRemoveFileCallback();

  const handleCancelRemoveFile = () => {
    setConfirmDialogOpen(false);
    setFileIdToDelete(null);
  };

  const handleConfirmRemoveFile = async () => {
    setConfirmDialogOpen(false);
    if (fileIdToDelete) {
      await getRemoveFileCallback(fileIdToDelete);
      reportQuery.refetch();
    }
  };

  const headerText = isDiagnosisEvaluation ? "Files" : "Assessment Files";
  const headerIcon = isDiagnosisEvaluation ? (
    <DocumentIcon className="text-limestone-700 h-4 w-4" />
  ) : (
    <DocumentChartBarIcon className="text-limestone-700 h-4 w-4" />
  );
  const topContent = isDiagnosisEvaluation ? null : (
    <div className="flex items-start bg-limestone-50 text-limestone-700 p-2 mt-2 rounded-sm">
      <div>
        <InformationCircleIcon className="h-4 w-4" />
      </div>
      <div className="ml-2 text-xs">Assessment files are for reference only and inform your report.</div>
    </div>
  );

  const collapsibleClientFirstName = report.reportClientDetails?.clientFirstName ?? "";
  const collapsibleClientLastName = report.reportClientDetails?.clientLastName ?? "";
  const collapsibleLearnerName = `${collapsibleClientFirstName} ${collapsibleClientLastName}`.trim() || undefined;

  return (
    <div className="w-full max-w-full">
      <FilesCollapsible
        files={assessmentFiles}
        topContent={topContent}
        bottomContent={
          isReportCompleted || isReadOnly ? null : (
            <Button
              text={
                <div className="flex items-center h-full w-full justify-center text-green-700">
                  <PlusIcon className="mr-2 h-4 w-4" />
                  <Small colorType="primary" className="text-green-700 text-xs">
                    {isDiagnosisEvaluation ? "Add Files" : "Add Assessment Files"}
                  </Small>
                </div>
              }
              appearance="secondary"
              className="ring-green-400 mt-5 min-w-full max-h-8 flex items-center justify-center"
              onClick={() => setAddAssessmentDialogOpen(true)}
            />
          )
        }
        headerIcon={headerIcon}
        headerText={headerText}
        downloadFileCallback={downloadReportFileById}
        removeFileCallback={isReportCompleted || isReadOnly ? undefined : handleRemoveFile}
        groupByDocumentType={isDiagnosisEvaluation}
        fileClickedCallback={handleFileClicked}
      />
      <Dialog
        title={isDiagnosisEvaluation ? "" : "Upload Assessment Files"}
        open={isAddAssessmentDialogOpen}
        setOpen={setAddAssessmentDialogOpen}
        secondaryButton={undefined}
        primaryButton={undefined}
        size={isDbBulkUploadEnabled ? "4xl" : "2xl"}
        overflowBehavior="auto"
        bodyClassName="min-h-[40vh] max-h-[90vh] overflow-y-auto"
        showCloseButton={isDbBulkUploadEnabled}
      >
        {isDbBulkUploadEnabled ? (
          <div className="pb-6">
            <UploadDocuments
              supportedAssessments={supportedAssessmentsList}
              existingFiles={existingFiles || []}
              reportType={report.reportType}
              fileUploadConfig={report.reportTemplate?.fileUploadConfig}
              reportId={report.id}
              learnerId={report.learner?.id}
              learnerName={collapsibleLearnerName}
            />
          </div>
        ) : (
          <AssessmentSelection existingAssessments={[]} reportDoneCallback={() => {}} existingAssessmentsTypes={[]} />
        )}
      </Dialog>
      <Dialog
        title="Confirm Remove File"
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        primaryButton={{
          buttonAction: "destructive",
          text: "Yes, Continue",
          onClick: () => handleConfirmRemoveFile(),
        }}
        secondaryButton={{ text: "No, Cancel", onClick: () => handleCancelRemoveFile() }}
        size="sm"
      >
        <Paragraph>Removing this file may affect ai-generated fields, would you like to continue?</Paragraph>
      </Dialog>
    </div>
  );
};
